import React from 'react';
import { Descriptions, Modal, Skeleton, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ATTRIBUTION_STATUS_COLORS, PAYOUT_STATUS_COLORS, getStatusColor } from './constants';
import { formatCurrency, formatDate } from './utils/formatters';
import CreatorProgramTable from './components/CreatorProgramTable';
import { SectionTitle } from './Styles';
import { usePayout } from './hooks/useCreatorQueries';
import type { Attribution, Payout } from './types';

const { Text } = Typography;

interface PayoutDetailModalProps {
  open: boolean;
  payout: Payout | null;
  onClose: () => void;
}

/**
 * §5.3 — payout detail.
 *
 * A receipt-style view: the payout header plus the exact attribution rows swept into
 * it. The list endpoint doesn't carry `attributions[]`, so this fetches the payout by
 * id when opened.
 */
const PayoutDetailModal: React.FC<PayoutDetailModalProps> = ({ open, payout, onClose }) => {
  const { data: detail, isLoading } = usePayout(open ? payout?.id : undefined);

  // Fall back to the list row while the detail request is in flight, so the header
  // renders immediately instead of flashing a skeleton for data we already have.
  const record = detail ?? payout;
  const attributions = detail?.attributions ?? [];

  const columns: ColumnsType<Attribution> = [
    {
      title: 'ORDER',
      key: 'orderId',
      width: 170,
      render: (_, row) => (
        <Text ellipsis style={{ maxWidth: 150 }}>
          {row.orderId}
        </Text>
      ),
    },
    {
      title: 'STORE',
      key: 'store',
      width: 140,
      render: (_, row) => row.store?.name || '—',
    },
    {
      title: 'CODE',
      key: 'code',
      width: 130,
      render: (_, row) => (row.code?.code ? <Tag>{row.code.code}</Tag> : <Tag color="purple">Link</Tag>),
    },
    {
      title: 'COMMISSION BASE',
      key: 'commissionBase',
      width: 155,
      render: (_, row) => formatCurrency(row.commissionBase),
    },
    {
      title: 'EARNINGS',
      key: 'creatorEarnings',
      width: 130,
      render: (_, row) => (
        <strong style={{ color: row.creatorEarnings < 0 ? '#cf1322' : undefined }}>
          {formatCurrency(row.creatorEarnings)}
        </strong>
      ),
    },
    {
      title: 'STATUS',
      key: 'status',
      width: 110,
      render: (_, row) => (
        <Tag color={getStatusColor(row.status, ATTRIBUTION_STATUS_COLORS)}>{row.status}</Tag>
      ),
    },
  ];

  return (
    <Modal
      title="Payout Detail"
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      destroyOnHidden
    >
      {!record ? (
        <Skeleton active />
      ) : (
        <>
          <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
            <Descriptions.Item label="Creator">
              {record.creator?.name || record.creatorId}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={getStatusColor(record.status, PAYOUT_STATUS_COLORS)}>
                {record.status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Amount">
              <strong>{formatCurrency(record.amount)}</strong>
            </Descriptions.Item>
            <Descriptions.Item label="Period">
              {formatDate(record.periodStart)} → {formatDate(record.periodEnd)}
            </Descriptions.Item>
            <Descriptions.Item label="Reference">
              {record.reference || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Paid at">{formatDate(record.paidAt)}</Descriptions.Item>
          </Descriptions>

          <SectionTitle style={{ marginTop: 24 }}>
            Attributions in this payout
          </SectionTitle>
          <CreatorProgramTable<Attribution>
            variant="nested"
            columns={columns}
            dataSource={attributions}
            rowKey="id"
            loading={isLoading}
            scroll={{ x: true, y: 400 }}
            emptyText="No attribution rows"
          />
        </>
      )}
    </Modal>
  );
};

export default PayoutDetailModal;
